'use client';

import React from 'react';
import { cn } from '@/lib/utils';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export function Input({
  label,
  error,
  hint,
  leftIcon,
  rightIcon,
  className,
  id,
  ...props
}: InputProps) {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label
          htmlFor={inputId}
          className="text-sm font-medium text-brand-gray-light"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-gray">
            {leftIcon}
          </span>
        )}
        <input
          id={inputId}
          className={cn(
            'w-full h-10 bg-brand-surface-2 border rounded-xl px-4 text-sm text-brand-white',
            'placeholder:text-brand-gray transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-brand-lime/50 focus:border-brand-lime',
            error
              ? 'border-brand-red focus:ring-brand-red/30'
              : 'border-brand-gray-muted hover:border-brand-gray',
            leftIcon && 'pl-10',
            rightIcon && 'pr-10',
            className
          )}
          {...props}
        />
        {rightIcon && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-gray">
            {rightIcon}
          </span>
        )}
      </div>
      {error && <p className="text-xs text-brand-red">{error}</p>}
      {hint && !error && <p className="text-xs text-brand-gray">{hint}</p>}
    </div>
  );
}

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export function Textarea({ label, error, className, id, ...props }: TextareaProps) {
  const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={textareaId} className="text-sm font-medium text-brand-gray-light">
          {label}
        </label>
      )}
      <textarea
        id={textareaId}
        className={cn(
          'w-full bg-brand-surface-2 border rounded-xl px-4 py-3 text-sm text-brand-white',
          'placeholder:text-brand-gray transition-all duration-200 resize-none',
          'focus:outline-none focus:ring-2 focus:ring-brand-lime/50 focus:border-brand-lime',
          error
            ? 'border-brand-red focus:ring-brand-red/30'
            : 'border-brand-gray-muted hover:border-brand-gray',
          className
        )}
        {...props}
      />
      {error && <p className="text-xs text-brand-red">{error}</p>}
    </div>
  );
}
